import * as React from 'react';
import classNames from 'classnames';
import {Link} from 'react-router-dom';
import {getClass} from '@discordapp/common/utils/StylesheetUtils';

import Spinner from './Spinner';

import styles from './Button.module.css';

export const ButtonColor = {
  TRANSPARENT: 'transparent',
  BLACK: 'black',
  BRAND: 'brand',
  WHITE: 'white',
  GREEN: 'green',
  RED: 'red',
  PRIMARY: 'primary',
  LINK: 'link',
};

export const ButtonLook = {
  FILLED: 'filled',
  OUTLINED: 'outlined',
  LINK: 'link',
  BLANK: 'blank',
};

export const ButtonHeight = {
  AUTO: 'auto',
  SHORT: 'short',
  MEDIUM: 'medium',
  TALL: 'tall',
};

export const ButtonStyles = {
  DISABLED: styles.disabled,
};

interface ButtonStyleProps {
  color?: Values<typeof ButtonColor>;
  look?: Values<typeof ButtonLook>;
  height?: Values<typeof ButtonHeight>;
  grow?: boolean;
  submitting?: boolean;
  disabled?: boolean;
  className?: string;
}

type BaseButtonProps = ButtonStyleProps & {
  children?: React.ReactNode;
  className?: string;
  rel?: string;
};

export function getButtonStyle({
  look = ButtonLook.FILLED,
  color = ButtonColor.BRAND,
  height = ButtonHeight.TALL,
  grow = true,
  submitting = false,
  disabled = false,
  className,
}: ButtonStyleProps): string {
  return classNames(
    styles.button,
    getClass(styles, look, color),
    getClass(styles, 'buttonHeight', height),
    {[styles.grow]: grow, [styles.disabled]: submitting || disabled},
    className,
  );
}

export type ButtonType = 'button' | 'submit' | 'reset';

export interface ButtonProps extends BaseButtonProps {
  'aria-label'?: string;
  download?: string;
  type?: ButtonType;
  target?: string;
  wrapContent?: boolean;
  onClick?: (a: React.SyntheticEvent<HTMLButtonElement>) => void;
  onMouseDown?: (a: React.SyntheticEvent<HTMLButtonElement>) => void;
}

export default function Button({
  color = ButtonColor.BRAND,
  grow = false,
  height = ButtonHeight.TALL,
  look = ButtonLook.FILLED,
  submitting = false,
  type = 'button',
  wrapContent = true,
  'aria-label': ariaLabel,
  className,
  disabled,
  download,
  onClick,
  onMouseDown,
  children,
}: ButtonProps) {
  function getButtonProps() {
    const forceDisabled = submitting || disabled;
    const sharedButtonProps = {
      'aria-label': ariaLabel,
      className: getButtonStyle({look, color, height, grow, submitting, disabled, className}),
      disabled: forceDisabled,
      download: forceDisabled ? null : download,
      onClick: forceDisabled ? undefined : onClick,
      onMouseDown: forceDisabled ? undefined : onMouseDown,
      type,
    };
    return sharedButtonProps;
  }

  function renderButtonContent() {
    if (!wrapContent) return children;
    const spinner = submitting ? <Spinner className={styles.spinner} type={Spinner.Type.PULSING_ELLIPSIS} /> : null;

    return (
      <div className={styles.contentWrapper}>
        {spinner}
        <span className={classNames(styles.buttonContent, {[styles.buttonContentHidden]: submitting})}>{children}</span>
      </div>
    );
  }

  const buttonProps = getButtonProps();
  return <button {...buttonProps}>{renderButtonContent()}</button>;
}

Button.Color = ButtonColor;
Button.Height = ButtonHeight;
Button.Look = ButtonLook;
Button.Styles = ButtonStyles;

interface LinkButtonProps extends BaseButtonProps {
  to: string;
  rel?: string;
  target?: string;
  onClick?: (a: React.SyntheticEvent<HTMLAnchorElement>) => void;
}

export function LinkButton({
  look = ButtonLook.FILLED,
  color = ButtonColor.BRAND,
  height = ButtonHeight.MEDIUM,
  grow = false,
  className,
  children,
  ...props
}: LinkButtonProps) {
  const fullClassName = getButtonStyle({look, color, height, grow, className});
  return (
    <Link {...props} className={fullClassName}>
      <span>{children}</span>
    </Link>
  );
}
